import React from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Circle,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  ChartOptions,
} from "chart.js";
import { motion } from "framer-motion";
import { FiPieChart } from "react-icons/fi";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { HeroTexture } from "../components/common/HeroTexture";
import { OmidexTrainingButton } from "../components/common/OmidexTrainingModal";
import {
  ProfileFundCard,
  ProfileFundCardEntry,
} from "../components/dashboard/ProfileFundCard";
import { FundCategoryFilter } from "../components/funds/FundCategoryFilter";
import {
  formatFundCurrency,
  formatFundNumber,
  isFundInvestmentEnabled,
} from "../components/funds/fundCardUtils";
import {
  type FundCategoryKey,
  filterFundsByCategory,
  isVisibleInDashboardFunds,
  isVisibleInFundCards,
  sortFundsByPriority,
} from "../config/fundCategories";
import { TadbirFund, useFunds } from "../hooks/useFunds";
import { tadbir } from "../services/api";

const MotionBox = motion(Box);

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
);

type ProfileFundEntry = ProfileFundCardEntry;

type ProfileResponse = {
  all: {
    totalAsset: number;
    totalAssetPercent?: number;
    totalProfit: number;
    totalUnits: number;
  };
  funds: ProfileFundEntry[];
};

const buildFallbackProfileFundEntry = (
  fund: TadbirFund,
): ProfileFundEntry => ({
  fundRegisterCode: fund.fundRegisterCode,
  fundName: fund.fundName,
  fundSummaryName: fund.fundSummaryName,
  fundTypeTitle: fund.fundTypeTitle,
  annualReturn: fund.annualReturn,
  nav: {
    subscriptionNav: fund.nav?.subscriptionNav || 0,
    cancelNav: fund.nav?.cancelNav || 0,
  },
  stats: {
    totalAsset: 0,
    totalProfit: 0,
    totalUnits: 0,
    summary: {
      groupName: fund.fundName,
      receivableDividendSum: 0,
    },
  },
});

type ChartRange = "1w" | "1m" | "1y";

const CHART_RANGE_OPTIONS: Array<{ value: ChartRange; label: string }> = [
  { value: "1w", label: "۱ هفته" },
  { value: "1m", label: "۱ ماه" },
  { value: "1y", label: "۱ سال" },
];

const LINE_COLORS = [
  "#AD0B06",
  "#2B6CB0",
  "#2F855A",
  "#D69E2E",
  "#805AD5",
  "#DD6B20",
];

const SummaryMetric = ({ label, value }: { label: string; value: string }) => {
  const heroIconBg = useColorModeValue("white", "icon.bg");
  const labelColor = useColorModeValue("gray.600", "whiteAlpha.700");
  const valueColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("blackAlpha.100", "whiteAlpha.200");

  return (
    <Box
      bg={heroIconBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="2xl"
      px={4}
      py={4}
      backdropFilter="blur(10px)"
      textAlign={"center"}
    >
      <Text fontSize="xs" color={labelColor} mb={2}>
        {label}
      </Text>
      <Text
        fontSize={{ base: "lg", md: "xl" }}
        fontWeight="extrabold"
        color={valueColor}
      >
        {value}
      </Text>
    </Box>
  );
};

const HomePageLoading = () => {
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Box
      h="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="20px"
    >
      <VStack spacing={4}>
        <Spinner size="xl" color="brand.500" thickness="4px" />
        <Text color={mutedColor}>در حال دریافت اطلاعات داشبورد...</Text>
      </VStack>
    </Box>
  );
};

export const HomePage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [chartRange, setChartRange] = React.useState<ChartRange>("1m");
  const [selectedCategory, setSelectedCategory] = React.useState<
    FundCategoryKey | "all"
  >("all");
  const pageBg = useColorModeValue("white", "dark.900");
  const heroBg = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.45), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249,168,166,0.45), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.22), transparent 50%)",
  );
  const heroText = useColorModeValue("gray.800", "white");
  const heroOverlay = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.45), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249,168,166,0.45), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.22), transparent 50%)",
  );
  const textColor = useColorModeValue("gray.800", "white");
  const gradientText = useColorModeValue(
    "linear(to-r, brand.500, brand.600)",
    "linear(to-r, brand.400, brand.500)",
  );

  const cardBg = useColorModeValue("white", "dark.50");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const heroIconBg = useColorModeValue("white", "icon.bg");
  const heroIconColor = useColorModeValue("brand.500", "icon.svg");
  const summaryCardBorder = useColorModeValue(
    "blackAlpha.100",
    "whiteAlpha.200",
  );
  const summaryLabelColor = useColorModeValue("gray.600", "whiteAlpha.700");
  const chartLegendColor = useColorModeValue("#4A5568", "#E2E8F0");
  const chartGridColor = useColorModeValue(
    "rgba(148, 163, 184, 0.24)",
    "rgba(255, 255, 255, 0.12)",
  );
  const chartRingColors = useColorModeValue(
    ["#2F855A", "#C53030", "#2B6CB0"],
    ["#68D391", "#FC8181", "#63B3ED"],
  );
  const { data: fundsData } = useFunds();
  const allFunds = React.useMemo(
    () =>
      (fundsData?.items || []).filter((fund) =>
        isVisibleInDashboardFunds(fund.fundRegisterCode),
      ),
    [fundsData],
  );

  const { data, isLoading, isError, error } = useQuery(
    ["tadbirProfile"],
    async () => {
      const response = await tadbir.getProfile();
      return response.profile as ProfileResponse;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: chartsData,
    isLoading: isChartsLoading,
    isError: isChartsError,
  } = useQuery(
    ["tadbirDashboardCharts", chartRange],
    () => tadbir.getDashboardCharts(chartRange),
    {
      refetchOnWindowFocus: false,
    },
  );

  const fundEntries = React.useMemo(
    () =>
      (data?.funds || []).filter((fund) =>
        isVisibleInDashboardFunds(fund.fundRegisterCode),
      ),
    [data],
  );
  const visibleFundEntries = React.useMemo(() => {
    const profileFundEntriesByCode = new Map(
      fundEntries.map((fundEntry) => [
        String(fundEntry.fundRegisterCode || "").trim(),
        fundEntry,
      ]),
    );

    if (!allFunds.length) {
      return fundEntries;
    }

    return allFunds.map((fund) => {
      const fundRegisterCode = String(fund.fundRegisterCode || "").trim();
      const profileEntry = profileFundEntriesByCode.get(fundRegisterCode);

      return {
        ...(profileEntry || buildFallbackProfileFundEntry(fund)),
        annualReturn: fund.annualReturn,
        fundSummaryName: fund.fundSummaryName,
      };
    });
  }, [allFunds, fundEntries]);
  const visibleCardFundEntries = React.useMemo(
    () =>
      sortFundsByPriority(
        filterFundsByCategory(
          visibleFundEntries.filter((fund) =>
            isVisibleInFundCards(fund.fundRegisterCode),
          ),
          selectedCategory,
        ),
      ),
    [selectedCategory, visibleFundEntries],
  );
  const doughnutData = React.useMemo(
    () => ({
      labels: fundEntries.map(
        (fund) =>
          fund.fundName?.trim() || fund.stats?.summary?.groupName || "صندوق",
      ),
      datasets: [
        {
          data: fundEntries.map((fund) => fund.stats?.totalAsset || 0),
          backgroundColor: chartRingColors,
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    }),
    [chartRingColors, fundEntries],
  );
  const doughnutOptions = React.useMemo<ChartOptions<"doughnut">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: chartLegendColor,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 18,
            font: {
              family: "inherit",
              size: 12,
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const fund = fundEntries[context.dataIndex];
              const percent = Number(
                fund?.stats?.totalAssetPercent || 0,
              ).toFixed(2);
              return `${context.label}: ${percent}%`;
            },
          },
        },
      },
    }),
    [chartLegendColor, fundEntries],
  );

  const balanceLineData = React.useMemo(
    () => ({
      labels: chartsData?.balanceChart?.labels || [],
      datasets: (chartsData?.balanceChart?.datasets || []).map(
        (dataset, index) => ({
          label: dataset.label,
          data: dataset.data,
          borderColor: LINE_COLORS[index % LINE_COLORS.length],
          backgroundColor: LINE_COLORS[index % LINE_COLORS.length],
          borderWidth: 2,
          tension: 0.24,
          spanGaps: true,
          pointRadius: 3,
          pointHoverRadius: 5,
        }),
      ),
    }),
    [chartsData],
  );

  const balanceLineOptions = React.useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "nearest",
        intersect: false,
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: chartLegendColor,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 18,
          },
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${formatFundCurrency(
                Number(context.parsed.y || 0),
              )} ریال`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: chartLegendColor,
          },
          grid: {
            color: chartGridColor,
          },
        },
        y: {
          ticks: {
            color: chartLegendColor,
            callback: (value) => Number(value).toLocaleString("fa-IR"),
          },
          grid: {
            color: chartGridColor,
          },
        },
      },
    }),
    [chartGridColor, chartLegendColor],
  );

  const requestBarData = React.useMemo(
    () => ({
      labels: chartsData?.requestChart?.labels || [],
      datasets: [
        {
          label: "صدور",
          data: chartsData?.requestChart?.issuanceAmounts || [],
          backgroundColor: "rgba(47, 133, 90, 0.78)",
          borderRadius: 10,
        },
        {
          label: "ابطال",
          data: chartsData?.requestChart?.redemptionAmounts || [],
          backgroundColor: "rgba(197, 48, 48, 0.78)",
          borderRadius: 10,
        },
      ],
    }),
    [chartsData],
  );

  const requestBarOptions = React.useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: chartLegendColor,
            usePointStyle: true,
            pointStyle: "rectRounded",
            padding: 18,
          },
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${formatFundCurrency(
                Number(context.parsed.y || 0),
              )} ریال`,
            afterLabel: (context) => {
              const counts =
                context.datasetIndex === 0
                  ? chartsData?.requestChart?.issuanceCounts
                  : chartsData?.requestChart?.redemptionCounts;
              const countValue = counts?.[context.dataIndex] || 0;
              return `تعداد درخواست: ${formatFundNumber(countValue)}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: chartLegendColor,
          },
          grid: {
            display: false,
          },
        },
        y: {
          ticks: {
            color: chartLegendColor,
            callback: (value) => Number(value).toLocaleString("fa-IR"),
          },
          grid: {
            color: chartGridColor,
          },
        },
      },
    }),
    [chartGridColor, chartLegendColor, chartsData],
  );

  const handleNavigateToInvest = (
    fundRegisterCode?: string,
    mode: "subscription" | "redemption" = "subscription",
    amountInRial?: number,
  ) => {
    if (!fundRegisterCode) {
      toast({
        title: "اطلاعات صندوق کامل نیست",
        description: "کد صندوق برای انتقال به صفحه سرمایه‌گذاری پیدا نشد.",
        status: "warning",
        duration: 2500,
        position: "top",
        isClosable: true,
      });
      return;
    }

    if (!isFundInvestmentEnabled(fundRegisterCode)) {
      toast({
        title: "سرمایه‌گذاری غیرفعال است",
        description: "در حال حاضر امکان سرمایه‌گذاری برای این صندوق وجود ندارد.",
        status: "info",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }

    const params = new URLSearchParams({ mode });
    if (Number.isFinite(amountInRial) && (amountInRial || 0) > 0) {
      params.set("amount", String(Math.floor(amountInRial || 0)));
    }

    navigate(`/funds/${fundRegisterCode}/invest?${params.toString()}`);
  };

  if (isLoading) {
    return <HomePageLoading />;
  }

  if (isError || !data) {
    return (
      <Box
        h="100%"
        overflowY="auto"
        px="20px"
        pt="20px"
        pb="28px"
        bgImage={pageBg}
      >
        <Container maxW="container.xl">
          <Alert status="error" borderRadius="2xl" alignItems="start">
            <AlertIcon />
            <Box>
              <AlertTitle mb={1}>دریافت اطلاعات داشبورد انجام نشد</AlertTitle>
              <AlertDescription>
                {(error as any)?.response?.data?.message ||
                  (error as Error | undefined)?.message ||
                  "در حال حاضر امکان دریافت پروفایل سرمایه‌گذاری وجود ندارد."}
              </AlertDescription>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box h="100%" overflowY="auto" bgImage={pageBg}>
      <VStack align="stretch" spacing={6}>
        <MotionBox
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, ease: "easeOut" }}
        >
          <Box
            bg={heroBg}
            color={heroText}
            borderRadius="3xl"
            overflow="hidden"
            position="relative"
            px={{ base: 5, md: 7 }}
            py={{ base: 6, md: 7 }}
            _before={{
              content: '""',
              position: "absolute",
              inset: 0,
              background: heroOverlay,
              zIndex: 0,
            }}
            boxShadow={"md"}
          >
            <HeroTexture />
            <VStack position="relative" align="stretch" spacing={6}>
              <VStack
                justify="space-between"
                align={"center"}
                spacing={4}
                flexWrap="wrap"
              >
                <Circle size={{ base: "72px", md: "84px" }} bg={heroIconBg}>
                  <Icon
                    as={FiPieChart as any}
                    boxSize={{ base: 8, md: 10 }}
                    color={heroIconColor}
                  />
                </Circle>

                <VStack align="center" textAlign="center" spacing={2}>
                  <Heading
                    as="h1"
                    fontSize={{ base: "2xl", md: "3xl" }}
                    fontWeight="900"
                    color={textColor}
                    lineHeight="short"
                  >
                    داشبورد سرمایه‌گذاری شما در
                    <Text
                      as="span"
                      display="block"
                      bgGradient={gradientText}
                      bgClip="text"
                    >
                      صندوق‌های امید
                    </Text>
                  </Heading>
                  <OmidexTrainingButton size="sm" />
                </VStack>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 3, xl: 3 }} spacing={3}>
                <SummaryMetric
                  label="تجمع اصل دارایی"
                  value={`${formatFundCurrency(data.all.totalAsset)} ریال`}
                />
                <SummaryMetric
                  label="تجمع اصل سود"
                  value={`${formatFundCurrency(data.all.totalProfit)} ریال`}
                />
                <SummaryMetric
                  label="تعداد واحدها"
                  value={`${formatFundNumber(data.all.totalUnits)} واحد`}
                />
              </SimpleGrid>
            </VStack>
          </Box>
        </MotionBox>

        {visibleFundEntries.length > 0 ? (
          <FundCategoryFilter
            funds={visibleFundEntries.filter((fund) =>
              isVisibleInFundCards(fund.fundRegisterCode),
            )}
            selectedCategory={selectedCategory}
            onChange={setSelectedCategory}
          />
        ) : null}

        {visibleCardFundEntries.length === 0 ? (
          <Box
            bg={cardBg}
            borderRadius="3xl"
            border="1px solid"
            borderColor={borderColor}
            p={8}
            textAlign="center"
          >
            <VStack spacing={3}>
              <Heading size="sm">صندوقی برای نمایش پیدا نشد</Heading>
              <Text color={mutedColor} maxW="560px">
                در حال حاضر داده‌ای از سرویس صندوق‌ها برنگشته است. پس از
                بارگذاری اطلاعات صندوق‌ها، کارت‌ها در همین صفحه نمایش داده
                می‌شوند.
              </Text>
            </VStack>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={5}>
            {visibleCardFundEntries.map((fundProfile, index) => {
              return (
                <ProfileFundCard
                  key={String(fundProfile.fundRegisterCode || index)}
                  fundProfile={fundProfile}
                  index={index}
                  mode="full"
                  onSubscription={(code) =>
                    handleNavigateToInvest(code, "subscription")
                  }
                  onRedemption={(code) =>
                    handleNavigateToInvest(code, "redemption")
                  }
                  onDetails={(code) => navigate("/funds/" + code)}
                />
              );
            })}
          </SimpleGrid>
        )}

        {fundEntries.length > 0 ? (
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={summaryCardBorder}
            borderRadius="3xl"
            px={{ base: 4, md: 6 }}
            py={{ base: 5, md: 6 }}
            boxShadow="md"
          >
            <VStack align="stretch" spacing={4}>
              <Text
                fontSize="sm"
                fontWeight="700"
                color={textColor}
                textAlign="center"
              >
                سهم هر صندوق از تجمع اصل دارایی
              </Text>
              <Text fontSize="xs" color={summaryLabelColor} textAlign="center">
                نمودار زیر درصد دارایی هر صندوق از کل دارایی شما را نشان می‌دهد.
              </Text>
              <Box h={{ base: "280px", md: "360px" }}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </Box>
            </VStack>
          </Box>
        ) : null}

        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={summaryCardBorder}
          borderRadius="3xl"
          px={{ base: 4, md: 6 }}
          py={{ base: 5, md: 6 }}
          boxShadow="md"
        >
          <VStack align="stretch" spacing={5}>
            <HStack
              justify="space-between"
              align={{ base: "start", md: "center" }}
              flexDirection={{ base: "column", md: "row" }}
              spacing={4}
            >
              <VStack align="start" spacing={1}>
                <Heading size="md">تحلیل گردش و درخواست‌ها</Heading>
              </VStack>

              <HStack spacing={2} flexWrap="wrap">
                {CHART_RANGE_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    size="sm"
                    borderRadius="full"
                    variant={chartRange === option.value ? "solid" : "outline"}
                    onClick={() => setChartRange(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </HStack>
            </HStack>

            {isChartsLoading ? (
              <Box py={12}>
                <VStack spacing={4}>
                  <Spinner size="lg" color="brand.500" thickness="4px" />
                  <Text color={mutedColor}>
                    در حال آماده‌سازی نمودارهای تحلیلی...
                  </Text>
                </VStack>
              </Box>
            ) : isChartsError ? (
              <Alert status="warning" borderRadius="2xl">
                <AlertIcon />
                در دریافت داده‌ی نمودارها خطایی رخ داد.
              </Alert>
            ) : (
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
                <Box
                  border="1px solid"
                  borderColor={summaryCardBorder}
                  borderRadius="2xl"
                  p={{ base: 4, md: 5 }}
                >
                  <VStack align="stretch" spacing={3}>
                    <Heading size="sm">نمودار موجودی مشتری</Heading>
                    <Box h={{ base: "280px", md: "340px" }}>
                      {balanceLineData.datasets.length > 0 ? (
                        <Line
                          data={balanceLineData}
                          options={balanceLineOptions}
                        />
                      ) : (
                        <VStack h="100%" justify="center" spacing={3}>
                          <Text color={mutedColor}>
                            داده‌ای برای این بازه پیدا نشد.
                          </Text>
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </Box>

                <Box
                  border="1px solid"
                  borderColor={summaryCardBorder}
                  borderRadius="2xl"
                  p={{ base: 4, md: 5 }}
                >
                  <VStack align="stretch" spacing={3}>
                    <Heading size="sm">
                      نمودار صدور و ابطال به تفکیک هر صندوق
                    </Heading>
                    <Box h={{ base: "280px", md: "340px" }}>
                      {requestBarData.labels.length > 0 ? (
                        <Bar
                          data={requestBarData}
                          options={requestBarOptions}
                        />
                      ) : (
                        <VStack h="100%" justify="center" spacing={3}>
                          <Text color={mutedColor}>
                            در این بازه درخواست صدور یا ابطالی ثبت نشده است.
                          </Text>
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </Box>
              </SimpleGrid>
            )}
          </VStack>
        </Box>
      </VStack>

    </Box>
  );
};
